import { handleHttpError } from './errorHandler';
import { checkTokenIsExpired } from '@/domains/authentication/services/authenticate';
import { t } from '@/translations';
import HttpError from '@/domains/common/typescript/HttpError';
import HttpStatusCodeEnum from '@/domains/common/typescript/HttpStatusCodeEnum';

class API {
    private apiUri: string;

    constructor() {
        this.apiUri = import.meta.env.VITE_API_URI;
    }

    public async getJson<T>(
        url: string,
        request?: RequestInit,
        headers?: Headers,
        options: { triggerNotification?: boolean } = { triggerNotification: true },
    ): Promise<T> {
        const requestInit = {
            headers,
            ...request,
            credentials: 'include',
        } as RequestInit;
        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        return API.responseBodyCheck(resp, options.triggerNotification);
    }

    public async getBlob(url: string, request?: RequestInit, headers?: Headers): Promise<Blob> {
        const requestInit = {
            headers,
            ...request,
            credentials: 'include',
        } as RequestInit;
        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        return API.responseBlobCheck(resp);
    }

    public async postJson<T>(url: string, request?: RequestInit, headers?: Headers): Promise<T> {
        const requestInit = {
            headers,
            ...request,
            credentials: 'include',
            method: 'POST',
        } as RequestInit;

        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        return API.responseBodyCheck(resp);
    }

    public async postBlob(url: string, request?: RequestInit, headers?: Headers): Promise<Blob> {
        const requestInit = {
            headers,
            ...request,
            credentials: 'include',
            method: 'POST',
        } as RequestInit;

        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        return resp.blob();
    }

    public async putJson<T>(url: string, request?: RequestInit, headers?: Headers): Promise<T> {
        const requestInit = {
            headers,
            ...request,
            credentials: 'include',
            method: 'PUT',
        } as RequestInit;

        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        return API.responseBodyCheck(resp);
    }

    public async postFormData<T>(url: string, body: FormData): Promise<T> {
        const requestInit = {
            body,
            credentials: 'include',
            method: 'POST',
        } as RequestInit;

        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        return API.responseBodyCheck(resp);
    }

    public async patchJson<T>(url: string, request?: RequestInit, headers?: Headers): Promise<T> {
        const requestInit = {
            headers,
            ...request,
            credentials: 'include',
            method: 'PATCH',
        } as RequestInit;

        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        return API.responseBodyCheck(resp);
    }

    public async deleteJson(url: string, request?: RequestInit, headers?: Headers): Promise<void> {
        const requestInit = {
            headers,
            ...request,
            credentials: 'include',
            method: 'DELETE',
        } as RequestInit;

        const resp = await fetch(`${this.apiUri}${url}`, requestInit);

        await API.responseBodyCheck(resp, false);
        // responseBodyCheck lets an empty-body error through (e.g. a gateway 5xx): it must not read as a success.
        if (!resp.ok) {
            throw new Error(t('api.500'));
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static async responseBodyCheck(resp: Response, triggerNotification = true): Promise<any> {
        const textBody = await resp.text();
        if (!textBody.length) {
            return null;
        }
        const jsonBody = JSON.parse(textBody);
        checkTokenIsExpired(jsonBody, resp.status);

        if (!resp.ok) {
            const message = jsonBody.message ?? t('api.500');
            if (resp.status === HttpStatusCodeEnum.FORBIDDEN.valueOf()) {
                throw new HttpError(resp.status, message);
            }
            if (triggerNotification) {
                handleHttpError(resp.status, message, true);
            }
            throw new Error(message);
        }

        if (resp.status === 400) {
            if (triggerNotification) {
                handleHttpError(resp.status, 'Serveur error 400', true);
            }
            throw new Error('Serveur error 400');
        }

        return jsonBody;
    }

    private static async responseBlobCheck(resp: Response, triggerNotification = true): Promise<Blob> {
        const blob = await resp.blob();

        if (!resp.ok) {
            const message = t('api.500');
            if (triggerNotification) {
                handleHttpError(resp.status, message, true);
            }
            throw new Error(message);
        }

        if (resp.status === 400) {
            if (triggerNotification) {
                handleHttpError(resp.status, 'Serveur error 400', true);
            }
            throw new Error('Serveur error 400');
        }

        return blob;
    }
}

const Api = new API();

export default Api;
